/**
 * Slack Notifications admin.
 *
 * Two things are configured here and they fail independently, so they are
 * shown independently: whether a workspace is connected at all, and where each
 * brand's messages go. A page that merged them would report "not set up" for a
 * connected workspace that simply has no routes yet.
 *
 * Every precondition the server can refuse on — the OAuth app, the encryption
 * key, the webhook secret — is surfaced as its own line rather than being
 * discovered by clicking into a 503.
 */

import { Fragment, useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Loader2, RefreshCw, AlertCircle, CheckCircle2, Send, Slack as SlackIcon, Play, Unplug, ChevronDown, ChevronRight,
} from 'lucide-react'

import { slackAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { MANIFOLD_TENANT_CLINICS } from '../utils/manifoldClinics'

// Velocity (and its alerts) is Science.co only; see backend services/velocity/liveAlerts.
// The same toggle switches on decline alerts, which any Manifold brand has.
const VELOCITY_ALERT_CLINICS = ['scienceco']
const ALERT_CLINICS = [...new Set([...VELOCITY_ALERT_CLINICS, ...MANIFOLD_TENANT_CLINICS])]
import { Card, CardContent, CardHeader, CardTitle, Button } from '../components/ui'

/** A precondition the server told us about, rendered as a one-line verdict. */
function Precondition({ ok, label, hint }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {ok
        ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
        : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />}
      <span className={ok ? 'text-muted-foreground' : ''}>
        {label}
        {!ok && hint && <span className="text-muted-foreground"> — {hint}</span>}
      </span>
    </div>
  )
}

function Toggle({ checked, onChange, label, disabled }) {
  return (
    <label className={`flex items-center gap-2 text-sm ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-input"
      />
      {label}
    </label>
  )
}

/** A Slack channel select, or a raw id box when the channel list could not load. */
function ChannelPicker({ channels, value, onChange, emptyLabel = '— none —' }) {
  if (channels.length === 0) {
    return (
      <input
        value={value || ''}
        onChange={e => onChange({ channelId: e.target.value, channelName: '' })}
        placeholder="C0123ABCDEF"
        className="w-56 rounded-md border border-input bg-background px-2 py-1 text-sm font-mono"
      />
    )
  }
  return (
    <select
      value={value || ''}
      onChange={e => {
        const picked = channels.find(c => c.id === e.target.value)
        onChange({ channelId: e.target.value, channelName: picked?.name || '' })
      }}
      className="w-56 rounded-md border border-input bg-background px-2 py-1 text-sm"
    >
      <option value="">{emptyLabel}</option>
      {channels.map(c => (
        <option key={c.id} value={c.id}>
          {c.isPrivate ? '🔒 ' : '#'}{c.name}
        </option>
      ))}
    </select>
  )
}

/** The workspace-wide channels, by purpose. Mirrors PurposeChannelSchema. */
const PURPOSES = [
  {
    key: 'alerts',
    label: 'Alerts',
    hint: 'Velocity and decline alerts for every brand that has them switched on, plus tech alerts. Unset, they go to each brand\'s own channel.',
  },
  {
    key: 'changelog',
    label: 'Changelog',
    hint: 'Posted by a super admin\'s agent through the MCP post_changelog tool.',
  },
]
const EMPTY_PURPOSE = { channelId: '', channelName: '', enabled: true }

/** A brand's per-type channels. Mirrors the route's `channels` on the server. */
const TYPE_CHANNELS = [
  { key: 'sales', label: 'New sales' },
  { key: 'summary', label: 'Daily summary' },
  { key: 'alerts', label: 'Alerts', hint: 'velocity + tech; blank uses the workspace Alerts channel, then the main one' },
  { key: 'changelog', label: 'Changelog', hint: 'post_changelog with this brand\'s clinicId' },
]

/** Whether a route has anywhere to post at all. */
const hasAnyChannel = route => Boolean(route?.channelId ||
  Object.values(route?.channels || {}).some(c => c?.channelId))

export default function SlackPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { clinics } = useAuthStore()

  const [config, setConfig] = useState(null)
  const [channels, setChannels] = useState([])
  const [routes, setRoutes] = useState([])
  const [purposeChannels, setPurposeChannels] = useState({})
  const [summary, setSummary] = useState({ enabled: false, hourEt: 9 })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [runningSummary, setRunningSummary] = useState(false)
  // Result of the last Run now, shown BESIDE the button. The page-level notice
  // sits at the top, and this button is near the bottom — clicking it while
  // scrolled down looked like nothing happened at all.
  const [summaryResult, setSummaryResult] = useState(null)
  const [testingChannel, setTestingChannel] = useState(null)
  const [expanded, setExpanded] = useState({})
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  // The OAuth callback bounces back here with the outcome in the query string,
  // since a redirect cannot carry a response body.
  useEffect(() => {
    const result = searchParams.get('slack')
    if (!result) return
    if (result === 'connected') setNotice({ kind: 'ok', text: 'Slack workspace connected.' })
    else if (result === 'denied') setNotice({ kind: 'warn', text: 'The Slack install was cancelled.' })
    else setNotice({ kind: 'error', text: `Slack install failed: ${searchParams.get('reason') || 'unknown'}` })
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete('slack'); next.delete('reason')
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams])

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const { data } = await slackAPI.getConfig()
      setConfig(data.data)
      setRoutes(data.data.routes || [])
      setPurposeChannels(data.data.channels || {})
      setSummary(data.data.dailySummary || { enabled: false, hourEt: 9 })

      if (data.data.connected) {
        // A failure here is not a page failure: the routing table is still
        // editable by pasting a channel id, which is what the picker sets.
        try {
          const res = await slackAPI.getChannels()
          setChannels(res.data.data.channels || [])
        } catch {
          setChannels([])
        }
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const routeFor = useCallback(
    clinicId => routes.find(r => r.clinicId === clinicId) || null,
    [routes]
  )

  const updateRoute = useCallback((clinicId, patch) => {
    setRoutes(prev => {
      const existing = prev.find(r => r.clinicId === clinicId)
      if (!existing) {
        return [...prev, {
          clinicId, channelId: '', channelName: '',
          newSales: true, dailySummary: true, velocityAlerts: false, enabled: true,
          ...patch,
        }]
      }
      return prev.map(r => (r.clinicId === clinicId ? { ...r, ...patch } : r))
    })
  }, [])

  const handleConnect = async () => {
    try {
      const { data } = await slackAPI.startOAuth()
      window.location.href = data.data.url
    } catch (err) {
      setNotice({ kind: 'error', text: err.response?.data?.error?.message || err.message })
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setNotice(null)
      const { data } = await slackAPI.saveConfig({ routes, dailySummary: summary, channels: purposeChannels })
      setConfig(data.data)
      setRoutes(data.data.routes || [])
      setPurposeChannels(data.data.channels || {})
      setNotice({ kind: 'ok', text: 'Saved.' })
    } catch (err) {
      setNotice({ kind: 'error', text: err.response?.data?.error?.message || err.message })
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async (channelId) => {
    try {
      setTestingChannel(channelId)
      await slackAPI.test(channelId)
      setNotice({ kind: 'ok', text: 'Test message sent.' })
    } catch (err) {
      const apiError = err.response?.data?.error
      // Slack's machine-readable code is far more actionable than the
      // sentence wrapped round it — "not_in_channel" tells you to invite the
      // bot, where "Slack API error" tells you nothing.
      setNotice({
        kind: 'error',
        text: apiError?.slackError
          ? `Slack said: ${apiError.slackError}`
          : (apiError?.message || err.message),
      })
    } finally {
      setTestingChannel(null)
    }
  }

  const handleRunSummary = async () => {
    try {
      setRunningSummary(true)
      setSummaryResult(null)
      const { data } = await slackAPI.runDailySummary()
      const results = data.data.results || []
      if (!data.data.ran) {
        // The run reads the SAVED config, so the commonest cause of "nothing
        // happened" is a toggle that is on screen but not yet saved.
        setSummaryResult({ kind: 'warn', text: `Not run — ${data.data.reason}.` })
      } else if (results.length === 0) {
        setSummaryResult({
          kind: 'warn',
          text: `Nothing to send for ${data.data.date} — no brand has a channel with Daily summary switched on.`,
        })
      } else {
        setSummaryResult({
          kind: 'ok',
          text: `${data.data.date}: ${results.map(r => `${r.clinicId} ${r.status}`).join(', ')}`,
        })
      }
    } catch (err) {
      setSummaryResult({
        kind: 'error',
        text: err.response?.data?.error?.message || err.message,
      })
    } finally {
      setRunningSummary(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      const { data } = await slackAPI.disconnect()
      setConfig(data.data)
      setChannels([])
      setNotice({ kind: 'ok', text: 'Workspace disconnected. Channel routing was kept.' })
    } catch (err) {
      setNotice({ kind: 'error', text: err.response?.data?.error?.message || err.message })
    }
  }

  // Unsaved channel routing. Run now reads the STORED routes, so a channel
  // picked but not saved will not receive anything — which looks identical to
  // the button doing nothing.
  const routesDirty = Boolean(
    config && JSON.stringify(config.routes) !== JSON.stringify(routes)
  )

  const sortedClinics = useMemo(
    () => [...(clinics || [])].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id)),
    [clinics]
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-5xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold">Slack Notifications</h1>
          <p className="text-xs text-muted-foreground">
            New-sale posts and the end-of-day summary per brand, plus alerts and changelog channels
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={load} className="h-9 w-9">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {notice && (
        <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
          notice.kind === 'ok' ? 'bg-emerald-50 text-emerald-800'
            : notice.kind === 'warn' ? 'bg-amber-50 text-amber-800'
            : 'bg-destructive/10 text-destructive'
        }`}>
          {notice.kind === 'ok'
            ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span>{notice.text}</span>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Workspace */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <SlackIcon className="w-4 h-4" /> Workspace
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {config?.connected ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <div className="font-medium">{config.installation.teamName || 'Connected'}</div>
                <div className="text-xs text-muted-foreground">
                  Token {config.installation.tokenHint}
                  {config.installation.installedByEmail && ` · installed by ${config.installation.installedByEmail}`}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleDisconnect}>
                <Unplug className="w-3.5 h-3.5 mr-1.5" /> Disconnect
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">No workspace connected.</p>
              <Button
                size="sm"
                onClick={handleConnect}
                disabled={!config?.oauthConfigured || !config?.secretsConfigured}
              >
                Connect Slack
              </Button>
            </div>
          )}

          <div className="pt-2 border-t space-y-1.5">
            <Precondition
              ok={config?.oauthConfigured}
              label="Slack app credentials"
              hint="SLACK_CLIENT_ID and SLACK_CLIENT_SECRET are not set on the server"
            />
            <Precondition
              ok={config?.secretsConfigured}
              label="Token encryption key"
              hint="SECRET_BOX_KEY is not set, so a bot token cannot be stored safely"
            />
            <Precondition
              ok={config?.webhookConfigured}
              label="Manifold webhook secret"
              hint="MANIFOLD_WEBHOOK_SECRET is not set, so inbound sale events are rejected"
            />
          </div>
        </CardContent>
      </Card>

      {/* Per-brand routing */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Channels by brand</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="px-4 py-2 font-medium">Brand</th>
                  <th className="px-4 py-2 font-medium">Channel</th>
                  <th className="px-4 py-2 font-medium">New sales</th>
                  <th className="px-4 py-2 font-medium">Daily summary</th>
                  <th className="px-4 py-2 font-medium" title="Decline alerts for any Manifold brand: checkout acceptance under 75% over 6 hours and clearly below its usual rate, or renewal acceptance clearly below usual over 24 hours. Plus, on Science.co, traffic, sales or spend stops from Velocity. Posts to the Alerts channel when one is set.">Alerts</th>
                  <th className="px-4 py-2 font-medium">On</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {sortedClinics.map(clinic => {
                  const route = routeFor(clinic.id)
                  const split = Object.values(route?.channels || {}).filter(c => c?.channelId).length
                  const open = Boolean(expanded[clinic.id])
                  return (
                    <Fragment key={clinic.id}>
                    <tr className={`${open ? '' : 'border-b last:border-0'} hover:bg-muted/40`}>
                      <td className="px-4 py-2 font-medium whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setExpanded(prev => ({ ...prev, [clinic.id]: !open }))}
                          className="inline-flex items-center gap-1"
                          title="Send each notification type to its own channel"
                        >
                          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          {clinic.name || clinic.id}
                        </button>
                        {split > 0 && (
                          <span className="ml-1.5 text-xs font-normal text-muted-foreground">{split} split</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <ChannelPicker
                          channels={channels}
                          value={route?.channelId}
                          onChange={patch => updateRoute(clinic.id, patch)}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Toggle
                          checked={route?.newSales !== false}
                          disabled={!hasAnyChannel(route)}
                          onChange={v => updateRoute(clinic.id, { newSales: v })}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Toggle
                          checked={route?.dailySummary !== false}
                          disabled={!hasAnyChannel(route)}
                          onChange={v => updateRoute(clinic.id, { dailySummary: v })}
                        />
                      </td>
                      <td className="px-4 py-2">
                        {ALERT_CLINICS.includes(clinic.id) ? (
                          <Toggle
                            checked={route?.velocityAlerts === true}
                            disabled={!hasAnyChannel(route)}
                            onChange={v => updateRoute(clinic.id, { velocityAlerts: v })}
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">–</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Toggle
                          checked={route?.enabled !== false}
                          disabled={!hasAnyChannel(route)}
                          onChange={v => updateRoute(clinic.id, { enabled: v })}
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        {route?.channelId && config?.connected && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTest(route.channelId)}
                            disabled={testingChannel === route.channelId}
                          >
                            {testingChannel === route.channelId
                              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              : <Send className="w-3.5 h-3.5 mr-1.5" />}
                            Test
                          </Button>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-b last:border-0 bg-muted/20">
                        <td />
                        <td colSpan={6} className="px-4 pb-3 pt-1">
                          <div className="grid gap-2 sm:grid-cols-2">
                            {TYPE_CHANNELS.map(type => (
                              <label key={type.key} className="text-xs space-y-1">
                                <span className="block text-muted-foreground">
                                  {type.label}{type.hint && <span> · {type.hint}</span>}
                                </span>
                                <ChannelPicker
                                  channels={channels}
                                  value={route?.channels?.[type.key]?.channelId}
                                  emptyLabel="— main channel —"
                                  onChange={patch => updateRoute(clinic.id, {
                                    channels: { ...(route?.channels || {}), [type.key]: patch },
                                  })}
                                />
                              </label>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Workspace-wide channels */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Channels by purpose</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {PURPOSES.map(purpose => {
                  const channel = purposeChannels[purpose.key] || EMPTY_PURPOSE
                  const update = patch => setPurposeChannels(prev => ({
                    ...prev, [purpose.key]: { ...EMPTY_PURPOSE, ...prev[purpose.key], ...patch },
                  }))
                  return (
                    <tr key={purpose.key} className="border-b last:border-0 hover:bg-muted/40 align-top">
                      <td className="px-4 py-2">
                        <div className="font-medium">{purpose.label}</div>
                        <div className="text-xs text-muted-foreground max-w-sm">{purpose.hint}</div>
                      </td>
                      <td className="px-4 py-2">
                        <ChannelPicker channels={channels} value={channel.channelId} onChange={update} />
                      </td>
                      <td className="px-4 py-2">
                        <Toggle
                          checked={channel.enabled !== false}
                          disabled={!channel.channelId}
                          onChange={v => update({ enabled: v })}
                          label="On"
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        {channel.channelId && config?.connected && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTest(channel.channelId)}
                            disabled={testingChannel === channel.channelId}
                          >
                            {testingChannel === channel.channelId
                              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              : <Send className="w-3.5 h-3.5 mr-1.5" />}
                            Test
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Summary schedule */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Daily summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-4">
            <Toggle
              checked={summary.enabled}
              onChange={v => setSummary(s => ({ ...s, enabled: v }))}
              label="Post an end-of-day summary"
            />
            <label className="flex items-center gap-2 text-sm">
              at
              <select
                value={summary.hourEt}
                onChange={e => setSummary(s => ({ ...s, hourEt: Number(e.target.value) }))}
                className="rounded-md border border-input bg-background px-2 py-1 text-sm"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, '0')}:00 ET</option>
                ))}
              </select>
              for the day before
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunSummary}
              disabled={runningSummary}
            >
              {runningSummary
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Play className="w-3.5 h-3.5 mr-1.5" />}
              {runningSummary ? 'Running…' : 'Run now'}
            </Button>
          </div>

          {summaryResult && (
            <div className={`flex items-start gap-2 rounded-lg p-2.5 text-sm ${
              summaryResult.kind === 'ok' ? 'bg-emerald-50 text-emerald-800'
                : summaryResult.kind === 'warn' ? 'bg-amber-50 text-amber-800'
                : 'bg-destructive/10 text-destructive'
            }`}>
              {summaryResult.kind === 'ok'
                ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
              <span>{summaryResult.text}</span>
            </div>
          )}

          {routesDirty && (
            <p className="text-xs text-amber-700">
              You have unsaved channel changes. Run now uses the saved routing, so save
              first or it will post to the previous channels.
            </p>
          )}

          <p className="text-xs text-muted-foreground">
            Checked every five minutes. A brand is summarised once per day — running it
            again, or a redeploy, will not post twice.
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save changes
        </Button>
      </div>

      <div className="text-xs text-muted-foreground space-y-1">
        <p>
          Sale posts carry the amount, the brand and the product — never a patient name,
          email or id. Renewals are not posted individually; they are counted in the daily
          summary and in the Daily Report.
        </p>
        <p>
          Individual sale posts currently require a Hyperswitch-billed charge. Manifold's
          new-vs-renewal signal is not valid for Chargebee, so Chargebee sales are reported
          as unclassified and stay out of the channel — which today means MDExam posts few
          individual sales. Its daily summary is unaffected and counts every charge.
        </p>
      </div>
    </div>
  )
}
